import { type FC, useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import serverVars from 'server-vars';
import { FormattedMessage } from 'dibs-react-intl';
import { useAuctionCountdown } from 'dibs-crypto-utils/exports/useAuctionCountdown';
import { type UnitMap } from 'dibs-crypto-utils/exports/useAuctionCountdown';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { useIsPrimarySale } from 'dibs-crypto-utils/exports/useIsPrimarySale';
import { useMarketSettings } from 'dibs-crypto-utils/exports/useMarketSettings';
import { useSplit } from 'dibs-crypto-utils/exports/useSplit';
import { EthereumUnsupportedChainError } from 'dibs-crypto-utils/exports/EthereumUnsupportedChainError';
import { EthereumMismatchedAddressError } from 'dibs-crypto-utils/exports/EthereumMismatchedAddressError';
import { ConnectMetaMaskButton } from 'dibs-crypto-utils/exports/ConnectMetaMaskButton';
import { ConnectWalletConnectButton } from 'dibs-crypto-utils/exports/ConnectWalletConnectButton';
import { shortAddress } from 'dibs-crypto-utils/exports/formattingUtils';
import Hourglass from 'dibs-icons/exports/legacy/Hourglass';
import ExclamationCircle from 'dibs-icons/exports/legacy/ExclamationCircle';
import { NftPdpPriceDisplay } from '../NftPdpPriceDisplay/NftPdpPriceDisplay';
import { NftPdpBidModal } from '../NftPdpBidModal/NftPdpBidModal';
import { EndAuctionModalLazy as EndAuctionModal } from 'dibs-nft-item-tile/exports/EndAuctionModalLazy';
import { learnMoreLink } from '../../helpers/priceHelpers';
import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import { useBidPrices } from '../helpers/useBidPrices';
import { NftPdpBidModalContext } from '../NftPdpBidModal/NftPdpBidModalContext';
import { NftPdpContractContext } from '../NftPdpContractContext/NftPdpContractContext';
import { NftPdpSplitComponent } from './NftPdpSplitComponent';
import { SplitDetailsModal } from '../../components/SplitModals/SplitDetailsModal';
import { NftAvatarCircle } from 'dibs-crypto-utils/exports/NftAvatarCircle';

import { type NftPdpLiveDetails_nftItem$key } from './__generated__/NftPdpLiveDetails_nftItem.graphql';
import { type NftPdpLiveDetails_viewer$key } from './__generated__/NftPdpLiveDetails_viewer.graphql';

import styles from './NftPdpLiveDetails.scss';

const CountDown: FC<{ countdownUnits: UnitMap | null }> = ({ countdownUnits }) => {
    if (!countdownUnits) {
        return null;
    }

    const unitsToShow: Array<keyof UnitMap> = [];

    Object.keys(countdownUnits).forEach(unit => {
        if (countdownUnits[unit as keyof UnitMap] > 0 || unitsToShow.length > 0) {
            unitsToShow.push(unit as keyof UnitMap);
        }
    });

    return (
        <dl className={styles.unitList}>
            {unitsToShow.map(unit => (
                <div className={styles.unitWrapper} key={unit}>
                    <dt className={styles.unitLabel}>{unit}</dt>
                    <dd className={styles.unitAmount}>{countdownUnits[unit]}</dd>
                </div>
            ))}
        </dl>
    );
};

type Props = {
    nftItem: NftPdpLiveDetails_nftItem$key;
    viewer: NftPdpLiveDetails_viewer$key;
};

export const NftPdpLiveDetails: FC<Props> = ({ nftItem: nftItemRef, viewer: viewerRef }) => {
    const isMobile = serverVars.get('settings.isMobile');
    const mobileUnsupportedBrowser = isMobile && !window.ethereum;
    const {
        openBidModal,
        updateModalType,
        isHighestBidder,
        isSeller,
        isOwner,
        isBidInFlight,
        auctionEndDate,
        auctionStatus,
        persistedAddress,
        hasMismatchedAddress,
        modalOpen,
        bidError,
        getConvertedEtherUnformatted,
    } = useContext(NftPdpBidModalContext);
    const { auctionContract, isAuctionGloballyPaused } = useContext(NftPdpContractContext);
    const {
        unsupportedChain,
        unsupportedWCChain,
        account: connectedAddress,
        connected,
        openConnectionForMismatch,
        openConnectionWithoutSigning,
        switchMetaMaskEthereumChain,
        isMetaMaskInstalled,
    } = useWeb3Connection();

    const nftItem = useFragment(
        graphql`
            fragment NftPdpLiveDetails_nftItem on NftItem {
                serviceId
                tokenId
                contract {
                    address
                }
                creatorAddress {
                    address
                }
                paymentAddresses {
                    ...useSplit_paymentAddresses
                    ...NftPdpSplitComponent_paymentAddresses
                    ...SplitDetailsModal_paymentAddresses
                }
                balanceForUser(userId: $userId) @include(if: $fetchUser) {
                    balance
                }
                mostRecentAuction {
                    auctionId
                    status
                    sellerAddress {
                        address
                    }
                    winnerAddress {
                        address
                    }
                    fundsRecipient {
                        ...useSplit_address
                        ...NftPdpSplitComponent_address
                        ...SplitDetailsModal_address
                    }
                    ...useBidPrices_nftAuction
                }
                isERC1155
                ...NftPdpPriceDisplay_nftItem
                ...NftPdpBidModal_nftItem
                ...useIsPrimarySale_nftItem
                ...SplitDetailsModal_nftItem
                ...useMarketSettings_nftItem
            }
        `,
        nftItemRef
    );
    const viewer = useFragment(
        graphql`
            fragment NftPdpLiveDetails_viewer on Viewer {
                nftBalancesByItem(nftId: $nftId) {
                    ownerAddress {
                        ...NftAvatarCircle_address
                        address
                        ensDomain
                        publicProfileUrl
                        publicProfile {
                            profileName
                        }
                    }
                }
                walletConnectFeatureFlag: featureFlag(feature: "allowWalletConnect")
                nftBenignNeglect: featureFlag(feature: "nftBenignNeglect")
                ...NftPdpBidModal_viewer
                ...EndAuctionModal_viewer
                ...SplitDetailsModal_viewer
                ...useMarketSettings_viewer
            }
        `,
        viewerRef
    );

    const isPrimarySale = useIsPrimarySale({ nftItem });

    const { walletConnectFeatureFlag, nftBalancesByItem } = viewer;
    const { mostRecentAuction, tokenId, contract, creatorAddress, paymentAddresses, isERC1155 } =
        nftItem;
    const { address: creator } = creatorAddress || {};
    const { address } = contract || {};
    const tokenTrackingString = `${address}/id/${tokenId}`;
    const { auctionId, winnerAddress, fundsRecipient } = mostRecentAuction || {};
    const { address: winner } = winnerAddress || {};
    const {
        address: owner,
        ensDomain,
        publicProfile,
        publicProfileUrl,
    } = nftBalancesByItem?.[0]?.ownerAddress || {};
    const ownerAddresses = nftBalancesByItem?.map(ownerValue => ownerValue?.ownerAddress?.address);
    const { profileName: displayName } = publicProfile || {};

    const { countdownUnits, countdownFinished: auctionCountdownFinished } = useAuctionCountdown(
        auctionEndDate,
        true
    );

    const [splitModalType, setSplitModalType] = useState<'SALE' | 'ROYALTY' | null>(null);
    const { hasSplit: hasSaleSplit } = useSplit({ singleAddress: fundsRecipient });
    const { hasSplit: hasRoyaltySplitBool } = useSplit({ addressList: paymentAddresses });
    const hasRoyaltySplit = !isPrimarySale && !!hasRoyaltySplitBool;

    const ownerName = displayName || ensDomain;
    const isPaused = auctionStatus === 'PAUSED' || !auctionStatus;
    const isLive = auctionStatus === 'LIVE' && !auctionCountdownFinished;
    const isEnded = auctionStatus === 'ENDED';
    const isCancelled = auctionStatus === 'CANCELED';
    const isBuyerClaiming = auctionCountdownFinished && isHighestBidder;
    const isSellerClaiming = auctionCountdownFinished && isSeller;
    const isAwaitingSettlement =
        !isEnded && auctionCountdownFinished && !isBuyerClaiming && !isSellerClaiming;

    const [endAuctionModalType, setEndAuctionModalType] = useState<'PENDING' | 'SUCCESS' | null>(
        null
    );
    const [endAuctionModalOpen, setEndAuctionModalOpen] = useState(false);
    useEffect(() => {
        if (endAuctionModalType === 'PENDING' && isEnded) {
            setEndAuctionModalType('SUCCESS');
        }
    }, [isEnded, endAuctionModalType]);

    const [endAuctionPending, setEndAuctionPending] = useState(false);
    const [hasEndAuctionError, setHasEndAuctionError] = useState(false);
    const endAuction = useCallback(async () => {
        if (auctionContract && auctionId) {
            try {
                setEndAuctionPending(true);
                const transaction = await auctionContract.endAuction(auctionId);
                setEndAuctionModalOpen(true);
                setEndAuctionModalType('PENDING');
                await transaction.wait();
                setEndAuctionPending(false);
            } catch (e: $TSFixMe) {
                setEndAuctionModalOpen(false);
                setEndAuctionPending(false);
                if (e.code !== 4001 /* silently fail user rejection */) {
                    setHasEndAuctionError(true);
                }
            }
        }
    }, [auctionContract, auctionId]);

    const { minEther } = useBidPrices({ nftAuction: mostRecentAuction });
    const convertedMinBid = getConvertedEtherUnformatted(`${minEther}`, 'USD');

    const hasActiveAuction =
        !!nftItem.mostRecentAuction?.status &&
        ['LIVE', 'PAUSED'].includes(nftItem.mostRecentAuction.status);
    const canManageNft =
        !!persistedAddress &&
        (nftItem?.balanceForUser?.balance ||
            (hasActiveAuction &&
                nftItem.mostRecentAuction.sellerAddress?.address?.toLowerCase() ===
                    persistedAddress.toLowerCase()));

    const wasTokenTransferredPreAuction =
        (!mostRecentAuction || isCancelled) &&
        (!ownerAddresses?.includes(creator) || !ownerAddresses?.includes(winner));

    const marketSettings = useMarketSettings({ nftItem, viewer });

    const { royaltyRate, commissionRate, basisPoints } = marketSettings || {
        royaltyRate: 0,
        commissionRate: 0,
        basisPoints: 10000,
    };

    const { saleEarnings, royaltyEarnings } = useMemo(() => {
        const saleBasisPoints = isPrimarySale
            ? basisPoints - commissionRate
            : basisPoints - commissionRate - royaltyRate;
        const sale = (saleBasisPoints / basisPoints) * 100;
        const royalty = (royaltyRate / basisPoints) * 100;
        return {
            saleEarnings: sale,
            royaltyEarnings: royalty,
        };
    }, [isPrimarySale, royaltyRate, commissionRate, basisPoints]);

    return (
        <>
            <NftPdpBidModal nftItem={nftItem} viewer={viewer} />
            <EndAuctionModal
                modalType={endAuctionModalType}
                modalOpen={endAuctionModalOpen}
                isBuyerClaiming={!!isBuyerClaiming}
                closeModal={() => {
                    setEndAuctionModalOpen(false);
                    if (endAuctionModalType === 'SUCCESS') {
                        setEndAuctionModalType(null);
                    }
                }}
                viewer={viewer}
            />
            <NftPdpPriceDisplay nftItem={nftItem} />
            {((!modalOpen && !!bidError) || isAuctionGloballyPaused) && (
                <div className={styles.bidErrorWrapper}>
                    <ExclamationCircle className={styles.exclamationIcon} />
                    <p>
                        {isAuctionGloballyPaused ? (
                            <FormattedMessage
                                id="nft.pdp.sidebar.globallyPaused"
                                defaultMessage="Auctions are currently paused"
                            />
                        ) : (
                            bidError
                        )}
                    </p>
                </div>
            )}
            {(isPaused || isCancelled) && !canManageNft && !wasTokenTransferredPreAuction && (
                <div className={styles.stayTunedWrapper}>
                    <Hourglass className={styles.hourglassIcon} />
                    <span className={styles.stayTunedLabel}>
                        <FormattedMessage
                            id="nft.pdp.sidebar.stayTuned"
                            defaultMessage="Stay Tuned"
                        />
                    </span>
                    <p className={styles.stayTunedBody}>
                        <FormattedMessage
                            id="nft.pdp.sidebar.notAvailable"
                            defaultMessage="This item is not available for auction yet. This page will update when additional details are available"
                        />
                    </p>
                </div>
            )}
            {isLive && (
                <div className={styles.ctaWrapper}>
                    {isHighestBidder && (
                        <div className={styles.noBiddingWrapper}>
                            <span className={styles.noBiddingLabel}>
                                <FormattedMessage
                                    id="nft.pdp.sidebar.youAreTheLeader.title"
                                    defaultMessage="You Are Currently the Highest Bidder"
                                />
                            </span>
                            <p className={styles.noBiddingCopy}>
                                <FormattedMessage
                                    id="nft.pdp.sidebar.youAreTheLeader.copy"
                                    defaultMessage="You may submit another bid only when you are outbid. {learnMoreLink}"
                                    values={{ learnMoreLink }}
                                />
                            </p>
                        </div>
                    )}

                    {isSeller && (
                        <div className={styles.noBiddingWrapper}>
                            <span className={styles.noBiddingLabel}>
                                <FormattedMessage
                                    id="nft.pdp.sidebar.youAreTheOwner.title"
                                    defaultMessage="You are the owner of this NFT."
                                />
                            </span>
                            <p className={styles.noBiddingCopy}>
                                <FormattedMessage
                                    id="nft.pdp.sidebar.youAreTheOwner.copy"
                                    defaultMessage="You cannot place a bid on NFTs you own."
                                />
                            </p>
                        </div>
                    )}

                    <Button
                        fullWidth
                        onClick={() => {
                            updateModalType();
                            openBidModal();
                            trackEvent({
                                event: 'e_ga_event',
                                category: 'NFT',
                                action: 'NFT bid entry',
                                label: tokenTrackingString,
                                value: convertedMinBid,
                                additional: {
                                    orderType: 'NFT bid',
                                },
                            });
                        }}
                        disabled={
                            isBidInFlight ||
                            isHighestBidder ||
                            isSeller ||
                            !!isAuctionGloballyPaused
                        }
                    >
                        <FormattedMessage
                            id="nft.pdp.sidebar.placeBid"
                            defaultMessage="Place a Bid"
                        />
                    </Button>
                </div>
            )}
            {!!canManageNft && !viewer.nftBenignNeglect && (
                <div className={styles.buttonContainer}>
                    {!hasActiveAuction && (
                        <Button fullWidth href={`/nft-create/${nftItem.serviceId}/list`}>
                            <FormattedMessage
                                id="nft.pdp.sidebar.createListing"
                                defaultMessage="Create Listing"
                            />
                        </Button>
                    )}

                    <Button fullWidth type="secondary" href={`/nft-create/${nftItem.serviceId}/`}>
                        <FormattedMessage
                            id="nft.pdp.sidebar.manageNft"
                            defaultMessage="Manage NFT"
                        />
                    </Button>
                </div>
            )}

            {isLive && (
                <>
                    {auctionEndDate ? (
                        <div className={styles.countdownWrapper}>
                            <span className={styles.countdownLabel}>
                                <FormattedMessage
                                    id="nft.pdp.sidebar.countdownLabel"
                                    defaultMessage="Auction Ends in"
                                />
                            </span>
                            <CountDown countdownUnits={countdownUnits} />
                        </div>
                    ) : (
                        <p className={styles.notStartedCopy}>
                            <FormattedMessage
                                id="nft.pdp.sidebar.notStartedCopy"
                                defaultMessage="The auction will begin once a bid for the reserve price or higher has been placed. {learnMoreLink}"
                                values={{ learnMoreLink }}
                            />
                        </p>
                    )}
                </>
            )}
            {!isERC1155 && (isEnded || wasTokenTransferredPreAuction) && (
                <div className={styles.ownedByWrapper}>
                    <NftAvatarCircle
                        address={nftBalancesByItem?.[0]?.ownerAddress}
                        wrapperClassName={styles.avatarContainer}
                        loading="lazy"
                        avatarSize={48}
                    />
                    <div>
                        <span className={styles.ownedByLabel}>
                            <FormattedMessage
                                id="nft.pdp.sidebar.ownerBy"
                                defaultMessage="Owned by"
                            />
                        </span>
                        <div className={styles.ownedByBody}>
                            <span className={styles.ownerName}>
                                {isOwner ? (
                                    <Link href={publicProfileUrl || ''}>
                                        <FormattedMessage
                                            id="nft.pdp.sidebar.owned by you"
                                            defaultMessage="You"
                                        />
                                    </Link>
                                ) : (
                                    <Link href={publicProfileUrl || ''}>
                                        {ownerName || shortAddress(owner || '')}
                                    </Link>
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            {!isEnded && (isBuyerClaiming || isSellerClaiming) && (
                <>
                    {(unsupportedChain || unsupportedWCChain) && (
                        <EthereumUnsupportedChainError
                            isMetaMaskInstalled={isMetaMaskInstalled}
                            switchMetaMaskEthereumChain={switchMetaMaskEthereumChain}
                        />
                    )}
                    {hasMismatchedAddress && persistedAddress && (
                        <EthereumMismatchedAddressError
                            persistedAddress={shortAddress(persistedAddress)}
                        />
                    )}
                    {!(unsupportedChain || unsupportedWCChain || hasMismatchedAddress) ? (
                        <>
                            <div className={styles.endAuctionNote}>
                                <strong>
                                    <FormattedMessage
                                        id="nft.pdp.sidebar.congrats"
                                        defaultMessage="Congratulations!"
                                    />
                                </strong>
                                {isBuyerClaiming ? (
                                    <FormattedMessage
                                        id="nft.pdp.sidebar.buyerClaim"
                                        defaultMessage="You are the auction winner. Please claim this item in order to add it to your collection."
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="nft.pdp.sidebar.sellerClaim"
                                        defaultMessage="Your item sold. Please end the auction so that the transaction can be finalized and you can receive funds."
                                    />
                                )}
                            </div>
                            {hasEndAuctionError && (
                                <div className={styles.bidErrorWrapper}>
                                    <ExclamationCircle className={styles.exclamationIcon} />
                                    <p>
                                        <FormattedMessage
                                            id="nft.pdp.liveDetails.endAuctionError"
                                            defaultMessage="There was an error in transferring the token. Please try again."
                                        />
                                    </p>
                                </div>
                            )}
                            {connectedAddress ? (
                                <Button
                                    fullWidth
                                    onClick={endAuction}
                                    disabled={endAuctionPending || !!isAuctionGloballyPaused}
                                >
                                    {isBuyerClaiming ? (
                                        <FormattedMessage
                                            id="nft.pdp.sidebar.claimNft"
                                            defaultMessage="Claim NFT"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="nft.pdp.sidebar.endAuction"
                                            defaultMessage="End Auction"
                                        />
                                    )}
                                </Button>
                            ) : (
                                <div className={styles.connectButtons}>
                                    <ConnectMetaMaskButton
                                        fullWidth
                                        connected={connected}
                                        onConnect={openConnectionWithoutSigning}
                                        metaMaskNotInstalled={!isMetaMaskInstalled}
                                        disableDownloadLink={!!mobileUnsupportedBrowser}
                                        onInstallClick={
                                            mobileUnsupportedBrowser
                                                ? () => setEndAuctionModalOpen(true)
                                                : undefined
                                        }
                                    />
                                    {walletConnectFeatureFlag && (
                                        <ConnectWalletConnectButton
                                            fullWidth
                                            connected={connected}
                                            onConnect={async () => {
                                                await openConnectionWithoutSigning(() => {},
                                                'walletconnect');
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={styles.connectButtons}>
                            <ConnectMetaMaskButton
                                fullWidth
                                connected={connected}
                                onConnect={openConnectionForMismatch}
                                useOpenCopy
                                metaMaskNotInstalled={!isMetaMaskInstalled}
                                disableDownloadLink={!!mobileUnsupportedBrowser}
                                onInstallClick={
                                    mobileUnsupportedBrowser
                                        ? () => setEndAuctionModalOpen(true)
                                        : undefined
                                }
                            />
                            {walletConnectFeatureFlag && (
                                <ConnectWalletConnectButton
                                    fullWidth
                                    connected={connected}
                                    onConnect={async () => {
                                        await openConnectionWithoutSigning(() => {},
                                        'walletconnect');
                                    }}
                                />
                            )}
                        </div>
                    )}
                </>
            )}
            {isAwaitingSettlement && (
                <div className={styles.noBidsWrapper}>
                    <strong className={styles.noBidsLabel}>
                        <FormattedMessage
                            id="nft.pdp.sidebar.notAcceptingBids"
                            defaultMessage="No Longer Accepting Bids"
                        />
                    </strong>
                    <FormattedMessage
                        id="nft.pdp.sidebar.awaitingSettlement"
                        defaultMessage="This auction is in the process of being settled."
                    />
                </div>
            )}
            {isLive && (hasSaleSplit || hasRoyaltySplit) && (
                <div className={styles.splitSectionWrapper}>
                    {hasSaleSplit && (
                        <NftPdpSplitComponent
                            singleAddress={fundsRecipient}
                            type="SALE"
                            handleViewDetailsClick={() => setSplitModalType('SALE')}
                        />
                    )}
                    {hasRoyaltySplit && (
                        <NftPdpSplitComponent
                            addressList={paymentAddresses}
                            type="ROYALTY"
                            handleViewDetailsClick={() => setSplitModalType('ROYALTY')}
                        />
                    )}
                    <SplitDetailsModal
                        addressList={paymentAddresses}
                        singleAddress={fundsRecipient}
                        viewer={viewer}
                        nftItem={nftItem}
                        isOpen={splitModalType !== null}
                        onClose={() => setSplitModalType(null)}
                        initialSplitType={splitModalType || undefined}
                        earningsPercentageObj={{ royaltyEarnings, saleEarnings }}
                    />
                </div>
            )}
        </>
    );
};
